/**
 * EditTextCommand - Command for editing text entities with undo/redo support.
 *
 * Supports both OdDbText and OdDbMText entities.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { getEntityHandle, findEntityByHandle } from "../core/oda";
import { getEntityType, EntityType } from "../entities/detection";
import { recordGraphicsModified } from "../entities";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

/**
 * Parameters for EditTextCommand.
 */
export interface EditTextParams {
  /** The text entity ID */
  entityId: DrawingModule.OdDbObjectId;
  /** The original text content */
  oldText: string;
  /** The new text content */
  newText: string;
}

/**
 * Command for editing text content with undo/redo support.
 */
export class EditTextCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private handle: string;
  private oldText: string;
  private newText: string;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: EditTextParams) {
    this.viewer = viewer;
    this.handle = getEntityHandle(params.entityId);
    this.oldText = params.oldText;
    this.newText = params.newText;
  }

  /**
   * Le texte est déjà changé avant la création de la commande — seul l'oplog est
   * émis ici (une seule fois, au push dans CommandHistory).
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      {
        handle: this.handle,
        changeKind: "text",
        oldValue: this.oldText,
        newValue: this.newText,
      },
    ]);
  }

  /**
   * Undo the text change.
   */
  undo(): void {
    this.setTextContent(this.oldText);
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  /**
   * Redo the text change.
   */
  redo(): void {
    this.setTextContent(this.newText);
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  /**
   * Dispose the command.
   */
  dispose(): void {
    // Nothing to dispose
  }

  /**
   * Set text content on the entity.
   */
  private setTextContent(text: string): void {
    const module = this.viewer.module;
    const entityId = findEntityByHandle(
      module,
      this.viewer.database,
      this.handle,
    );
    if (!entityId) return;

    const entityType = getEntityType(module, entityId);

    if (entityType === EntityType.Text) {
      const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
      const textEntity = module.OdDbText.cast(pObj);
      deleteObj(pObj);
      if (textEntity) {
        const textStr = new module.OdString(text);
        textEntity.setTextString(textStr);
        deleteObj(textStr);
        deleteObj(textEntity);
      }
    } else if (entityType === EntityType.MText) {
      const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
      const mtext = module.OdDbMText.cast(pObj);
      deleteObj(pObj);
      if (mtext) {
        const textStr = new module.OdString(text);
        mtext.setContents(textStr);
        deleteObj(textStr);
        deleteObj(mtext);
      }
    } else if (entityType === EntityType.MLeader) {
      const entity = entityId.safeOpenObject(OpenMode.kForWrite, false);
      const mleader = module.OdDbMLeader.cast(entity);
      if (mleader) {
        const mtext = mleader.mtext();
        if (mtext) {
          const textStr = new module.OdString(text);
          mtext.setContents(textStr);
          deleteObj(textStr);
          mleader.setMText(mtext);
          deleteObj(mtext);
        }
        deleteObj(mleader);
      }
      deleteObj(entity);
    }
    recordGraphicsModified(module, entityId);
    deleteObj(entityId);
  }
}
